import { useCallback, useRef } from 'react';
import { get } from 'lodash';
import InfrastructureListBase from '../infrastructure/InfrastructureListBase';
import { useActionContext } from '../infrastructure/hooks/UseActionContext';
import {
    ID_MAPPING,
    MONITORED_INTERFACE_ADMIN_LIST_DATA_EMPTY_TEXT,
    MONITORED_INTERFACE_DATA_SOURCE,
    MONITORED_INTERFACE_INITIAL_SORT_FIELD_INFO,
    MONITORED_INTERFACES_ADMIN_LIST_DATA_TABLE_ID,
    MONITORED_INTERFACES_FUNCTION_NAME,
    MONITORED_INTERFACES_TITLE,
    QUERY_LOADER_OPTION,
} from '../infrastructure/InfrastructureConstants';
import { buildPageId } from '../AdminLinkUtils';
import { MONITORED_INTERFACE_COLUMNS } from '../infrastructure/interfaces/InterfaceConstants';
import { INTERFACE_ACTION_URLS, INTERFACE_PRIORITY_LOW_URL } from '../infrastructure/device/DeviceConstants';
import { buildBulkActionPayload } from '../infrastructure/InfrastructureActionHelper';
import { useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import { getErrMessage } from '../../../utilities/PromiseUtilities';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';
import PollingStatusSummary from './PollingStatusSummary';
import { InterfaceActionMenusArray } from '../infrastructure/interfaces/InterfaceActionMenusBox';

export default function MonitoredInterfaceList() {
    const [, toastActions] = useUiToasts();
    const dataService = useDataService();
    const [, alertActions] = useAlerts();
    const refresherRef = useRef(null);
    const paramsGetRef = useRef(null);
    const { contextRef: actionContextRef, updateContext: updateActionContext } = useActionContext();

    /**
     * Handle interface menu actions (Polling/Priority/Speed).
     * Uses simple bulk action payload format without query params.
     * Refreshes table on success, shows error toast on failure.
     */
    const handleInterfaceMenuAction = useCallback(
        async (actionUrl, extraPayload = {}) => {
            try {
                const { tableSelection } = actionContextRef.current;
                const body = buildBulkActionPayload(tableSelection, extraPayload);
                // Pass in search and filterState(query info)
                const params = paramsGetRef.current?.buildParams();
                const response = await dataService.post(actionUrl, body, params);
                // After menu action is successful, call the refresher function provided by the table to update the data
                refresherRef.current?.();
                if (INTERFACE_PRIORITY_LOW_URL === actionUrl) {
                    const wanCount = get(response, 'wanCount', -1);
                    if (wanCount > 0) {
                        toastActions.createToast(
                            createToastConfig(
                                INFO,
                                'WAN interfaces are always High priority - one or more were not set to Low'
                            )
                        );
                    }
                }
            } catch (error) {
                alertActions.createErrorAlert(getErrMessage(error));
            }
        },
        [actionContextRef, dataService, toastActions, alertActions]
    );

    const renderActionButtons = useCallback(
        ({ isDataTableActionButton, tableSelection, tableState, handleConfirmedDataTableAction, refresher }) => {
            updateActionContext({
                handleConfirmedAction: handleConfirmedDataTableAction,
                tableSelection,
                tableState,
            });
            refresherRef.current = refresher;

            return (
                <InterfaceActionMenusArray
                    actionUrls={INTERFACE_ACTION_URLS}
                    disabled={isDataTableActionButton}
                    onAction={handleInterfaceMenuAction}
                />
            );
        },
        [handleInterfaceMenuAction, updateActionContext]
    );

    return (
        <InfrastructureListBase
            title={MONITORED_INTERFACES_TITLE}
            pageId={buildPageId(MONITORED_INTERFACES_FUNCTION_NAME)}
            tableId={MONITORED_INTERFACES_ADMIN_LIST_DATA_TABLE_ID}
            dataSource={MONITORED_INTERFACE_DATA_SOURCE}
            columns={MONITORED_INTERFACE_COLUMNS}
            sorters={MONITORED_INTERFACE_INITIAL_SORT_FIELD_INFO}
            emptyText={MONITORED_INTERFACE_ADMIN_LIST_DATA_EMPTY_TEXT}
            idMapping={ID_MAPPING}
            loaderOptions={QUERY_LOADER_OPTION}
            summaryElement={<PollingStatusSummary />}
            actionButtonsFn={renderActionButtons}
            apiRef={paramsGetRef}
        />
    );
}
